/**
 * The compiled level content.
 *
 * Only the public half of `content/generated` is imported here: the reference
 * solutions live in `solutions.json`, which the acceptance tests read and the
 * application never does, so no answer can leak into the shipped bundle.
 */

import data from '@levels';
import { expandTerm } from '@packed';
import type { Chapter, Level, LevelFile } from '@spec';

/**
 * The levels, with the packed case terms expanded.
 *
 * The generated file writes a numeral as its digits and a literal list between
 * brackets, because most of the content is unary numerals and cons chains and
 * writing them out costs the interface hundreds of kilobytes of parsing. The
 * terms reach the engine as the engine's own syntax, and the expansion is done
 * once, here, so nothing downstream has to know the packed form exists.
 */
const expanded = data as unknown as LevelFile;
for (const chapter of expanded.chapters) {
  for (const level of chapter.levels) {
    for (const entry of level.cases) {
      entry.lhs = expandTerm(entry.lhs);
      entry.rhs = expandTerm(entry.rhs);
      if (entry.also !== undefined) {
        entry.also = entry.also.map(expandTerm);
      }
    }
  }
}

export const content = expanded;

export const levels: Level[] = content.chapters.flatMap((chapter) => chapter.levels);

export const levelById = new Map<string, Level>(levels.map((level) => [level.id, level]));

export const chapterOf = new Map<string, Chapter>(
  content.chapters.flatMap((chapter) => chapter.levels.map((level) => [level.id, chapter])),
);

/** Positions of every level, used to walk to the next or previous one. */
const levelOrder = new Map<string, number>(levels.map((level, index) => [level.id, index]));

const chapterById = new Map(content.chapters.map((chapter) => [chapter.id, chapter]));

/** The title of a chapter, for messages that have to name one. */
export function chapterTitle(chapterId: string): string | undefined {
  return chapterById.get(chapterId)?.title;
}

/** The level's position in teaching order, counting from 1. */
export function positionOf(id: string): number {
  return (levelOrder.get(id) ?? -1) + 1;
}

export function nextLevelId(id: string): string | undefined {
  const index = levelOrder.get(id);
  if (index === undefined) {
    return undefined;
  }
  return levels[index + 1]?.id;
}

export function previousLevelId(id: string): string | undefined {
  const index = levelOrder.get(id);
  if (index === undefined || index === 0) {
    return undefined;
  }
  return levels[index - 1]?.id;
}
